/**
 * HollyTransportationCard — price-quote result card for the Holly chat.
 *
 * Layout (matches the reference design):
 *   - Top row: FROM (label + short city) · blue arrow · TO (label + short city)
 *   - Price range, bold and centered
 *   - Two info pills: Load · Service
 *   - Italic disclaimer
 *
 * Pure presentational — no `useHolly`, no click handlers, no detail panel.
 */

import type { HollyTransportationQuote } from './types';

interface HollyTransportationCardProps {
  quote: HollyTransportationQuote;
}

const DEFAULT_DISCLAIMER =
  'Final price may vary with timing and access details.';

function ArrowIcon() {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#3574E3'
      strokeWidth={2}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <line x1='5' y1='12' x2='19' y2='12' />
      <polyline points='13 6 19 12 13 18' />
    </svg>
  );
}

export default function HollyTransportationCard({
  quote,
}: HollyTransportationCardProps) {
  const priceRange = `From ${quote.currency}${quote.priceMin}`;
  const disclaimer = quote.disclaimer ?? DEFAULT_DISCLAIMER;

  return (
    <div
      className='flex flex-col gap-3 w-full p-4 border border-[#EAEBF0] rounded-lg bg-white shadow-sm'
      style={{ fontFamily: "'Poppins', sans-serif" }}
      aria-label={`Quote from ${quote.fromCity} to ${quote.toCity}`}
    >
      <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-2'>
        <div className='flex flex-col items-center'>
          <span className='text-[11px] font-medium tracking-[0.12em] text-[#9CA3AF]'>
            FROM
          </span>
          <span className='text-lg font-semibold text-[#1E1E2F]'>
            {quote.fromCityShort}
          </span>
        </div>
        <ArrowIcon />
        <div className='flex flex-col items-center'>
          <span className='text-[11px] font-medium tracking-[0.12em] text-[#9CA3AF]'>
            TO
          </span>
          <span className='text-lg font-semibold text-[#1E1E2F]'>
            {quote.toCityShort}
          </span>
        </div>
      </div>

      <div className='text-center text-3xl font-bold text-[#1E1E2F]'>
        {priceRange}
      </div>

      <div className='grid grid-cols-2 gap-2'>
        <div className='rounded-md bg-[#F3F4F6] p-3 text-sm text-[#1E1E2F]'>
          <span className='font-semibold'>Load:</span> {quote.loadDescription}
        </div>
        <div className='rounded-md bg-[#F3F4F6] p-3 text-sm text-[#1E1E2F]'>
          <span className='font-semibold'>Service:</span>{' '}
          {quote.serviceDescription}
        </div>
      </div>

      <p className='text-xs italic text-gray-500 m-0'>{disclaimer}</p>
    </div>
  );
}
